import React from 'react'
import { useNavigate } from 'react-router-dom'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import { NavigationProvider } from 'modules/core/lib/navigation/provider'

import type { DefaultParams } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import { IncidenceFilterProvider } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { MessageFilterProvider } from 'modules/bookkeepers/lib/MessageFilterContext'
import { FACTORIAL_APP, toIncidencesSettings } from 'modules/bookkeepers/lib/amplitudeData'
import { SORT_ATTRIBUTES } from 'modules/bookkeepers/lib/tableSortColumns'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import { INHOUSE_INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'

import InHouseIncidencesPage from './components/_InHouseIncidencePage'
import ProIncidencesPage from './components/_ProIncidencePage'

const IncidencesGuard = ({
  hasInhousePayroll,
  hasProfessionals,
  children,
}: {
  hasInhousePayroll: boolean
  hasProfessionals: boolean
  children: React.ReactNode
}) => {
  if (!hasInhousePayroll && !hasProfessionals) return <NotFound />

  return <>{children}</>
}

const IncidencesPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { hasInhousePayroll } = useInhousePayroll()
  const hasProfessionals = hasFeature('factorial_professionals')
  const { hasCompensations } = useHasCompensations()

  const {
    company: { name: companyName },
  } = useCurrentSession()

  const defaultIncidencesFilterValues: DefaultParams = {
    filters: {
      status: hasInhousePayroll
        ? INHOUSE_INCIDENCE_STATUSES.to_do
        : INCIDENCE_STATUSES.in_preparation,
    },
    direction: 'asc',
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  const defaultMessagesFilterValues: DefaultParams = {
    direction: 'desc',
    containsMessage: true,
    attribute: SORT_ATTRIBUTES.incidence_effective_date,
  }

  const navigateToManageIncidences = (source: string) => {
    track(trackingEvents.bookkeeper.incidences.goToIncidencesSettings, {
      application: FACTORIAL_APP,
      company_name: companyName,
      has_professionals: hasProfessionals,
      has_in_house_payroll: hasInhousePayroll,
      source: source,
    })

    return hasCompensations
      ? navigate(navigation.settings.compensations.employeeUpdates.fullPath())
      : navigate(navigation.settings.payroll_bundle.incidences.fullPath())
  }

  return (
    <IncidencesGuard hasInhousePayroll={hasInhousePayroll} hasProfessionals={hasProfessionals}>
      <ResponsivePage.Body
        showHeader={false}
        actions={{
          primary: [
            {
              icon: 'settings',
              label: '',
              variant: 'secondary',
              onClick: () => navigateToManageIncidences(toIncidencesSettings.fromButton),
            },
          ],
        }}
      >
        <NavigationProvider>
          <IncidenceFilterProvider defaultFilterValues={defaultIncidencesFilterValues}>
            <MessageFilterProvider defaultFilterValues={defaultMessagesFilterValues}>
              {hasInhousePayroll && <InHouseIncidencesPage />}
              {hasProfessionals && <ProIncidencesPage />}
            </MessageFilterProvider>
          </IncidenceFilterProvider>
        </NavigationProvider>
      </ResponsivePage.Body>
    </IncidencesGuard>
  )
}

export default IncidencesPage
